import { startMultipleAnalysis } from "api/layer";
import { AppAction, IAppArguments } from "helpers/context";
import {
  reportHazardLayerStatistics,
  reportPerilLayerStatistics,
  buildLocationCoordinates
} from "helpers/layerStatistics";
import { getLanguage } from "helpers/translations";
import { Dispatch } from "react";

export interface ClimateRiskScoreItem {
  index: string;
  scenario?: string;
}

export interface MultipleAnalysisLocation {
  id: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

export interface MultipleAnalysisParams {
  eventLayers?: string[];
  catNetLayers?: string[];
  responseStructure?: string[];
  climateRiskScores?: ClimateRiskScoreItem[];
  longitude?: number | string;
  latitude?: number | string;
  country?: string;
  oversea?: boolean;
  spatial?: string;
  locations?: MultipleAnalysisLocation[];
}

export function syncMultipleAnalysisRunningStatus(
  params: MultipleAnalysisParams,
  dispatch: Dispatch<IAppArguments>
) {
  if (params.eventLayers?.length) {
    dispatch({ type: AppAction.setEventAnalysisStatus, payload: "running" });
    dispatch({ type: AppAction.setJobId, payload: undefined });
  } else {
    dispatch({ type: AppAction.setEventAnalysisStatus, payload: undefined });
  }

  if (params.catNetLayers?.length) {
    dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "running" });
    dispatch({ type: AppAction.setJobId, payload: undefined });
  } else {
    dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: undefined });
  }

  if (params.climateRiskScores?.length) {
    dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "running" });
    dispatch({ type: AppAction.setJobId, payload: undefined });
  } else {
    dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: undefined });
  }
}

export function executeMultipleAnalysisWithReporting(
  params: MultipleAnalysisParams,
  dispatch: Dispatch<IAppArguments>
) {
  const hasHazard = !!params.catNetLayers?.length;
  const hasClimate = !!params.climateRiskScores?.length;
  const hasEvent = !!params.eventLayers?.length;

  if (!hasHazard && !hasClimate && !hasEvent) {
    return;
  }

  const batchCoordinates = buildLocationCoordinates(params.locations);
  const batchLocationAmount = params.locations?.length;

  const reportHazard = (
    status: "0" | "1",
    executeInterval: number,
    traceInfo?: string
  ) => {
    if (!hasHazard) {
      return;
    }
    reportHazardLayerStatistics({
      eventType: "batch",
      status,
      layers: params.catNetLayers,
      locationAmount: batchLocationAmount,
      executeInterval,
      coordinates: batchCoordinates,
      traceInfo
    });
  };

  const reportClimate = (
    status: "0" | "1",
    executeInterval: number,
    locationAmount?: number,
    traceInfo?: string
  ) => {
    if (!hasClimate) {
      return;
    }
    reportPerilLayerStatistics({
      eventType: "batch",
      perils: "crs",
      status,
      layerAmount: params.climateRiskScores?.length || 0,
      layers: params.climateRiskScores?.map((item) => item.index),
      locationAmount,
      executeInterval,
      coordinates: batchCoordinates,
      traceInfo
    });
  };

  const reportEvent = (
    status: "0" | "1",
    executeInterval: number,
    locationAmount?: number,
    traceInfo?: string
  ) => {
    if (!hasEvent) {
      return;
    }
    reportPerilLayerStatistics({
      eventType: "batch",
      perils: "event",
      status,
      layerAmount: params.eventLayers?.length || 0,
      layers: params.eventLayers,
      locationAmount,
      executeInterval,
      coordinates: batchCoordinates,
      traceInfo
    });
  };

  const startTime = Date.now();
  startMultipleAnalysis(params)
    .then((res: any) => {
      const responseTime = Date.now() - startTime;

      if (!res?.code || res.code === 400) {
        reportHazard("0", responseTime, res?.message);
        reportClimate("0", responseTime, batchLocationAmount, res?.message);
        reportEvent("0", responseTime, batchLocationAmount, res?.message);

        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "tip",
            titleIconColor: "red",
            titleIcon: "error",
            resMessage:
              getLanguage() === "en"
                ? res?.data?.messageEN ?? res?.message
                : res?.data?.messageCN ?? res?.message
          }
        });

        dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
        dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
        return;
      }

      if (res?.code === 200 && res?.data?.jobId) {
        reportHazard("1", responseTime);
        reportClimate("1", responseTime, batchLocationAmount);
        reportEvent("1", responseTime, batchLocationAmount);
        dispatch({ type: AppAction.setJobId, payload: res.data.jobId });
        return;
      }

      dispatch({ type: AppAction.setJobId, payload: undefined });
      if (hasHazard) {
        reportHazard("0", responseTime, res?.message);
        dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
      }
      if (hasClimate) {
        reportClimate("0", responseTime, batchLocationAmount, res?.message);
        dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
      }
      if (hasEvent) {
        reportEvent("0", responseTime, batchLocationAmount, res?.message);
        dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
      }
    })
    .catch((error: any) => {
      const responseTime = Date.now() - startTime;

      dispatch({ type: AppAction.setJobId, payload: undefined });
      if (hasHazard) {
        reportHazard("0", responseTime, error?.message);
        dispatch({ type: AppAction.setHazardMultipleAnalysisStatus, payload: "error" });
      }
      if (hasClimate) {
        reportClimate("0", responseTime, batchLocationAmount, error?.message);
        dispatch({ type: AppAction.setClimateMultipleAnalysisStatus, payload: "error" });
      }
      if (hasEvent) {
        reportEvent("0", responseTime, batchLocationAmount, error?.message);
        dispatch({ type: AppAction.setEventAnalysisStatus, payload: "error" });
      }
    });
}
